import {
    IonButton,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
    IonTextarea,
    useIonAlert,
    useIonToast
} from '@ionic/react';
import { checkmarkCircle, copyOutline, keyOutline, trashOutline } from 'ionicons/icons';
import { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { UserApiKey } from '../models/UserApiKey';
import {
    useCreateUserApiKeyMutation,
    useRevokeUserApiKeyMutation
} from '../query/userApiKeysQuery';
import styles from './ApiKeysForm.module.css';

interface ApiKeysFormProps {
    apiKeys: UserApiKey[];
}

function getErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'object' && error !== null && 'message' in error) {
        return String((error as { message: unknown }).message);
    }
    return fallback;
}

async function copyText(value: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();

    try {
        if (!document.execCommand('copy')) {
            throw new Error('Copy command failed.');
        }
    } finally {
        document.body.removeChild(textArea);
    }
}

const ApiKeysForm: React.FC<ApiKeysFormProps> = ({ apiKeys }) => {
    const { t } = useTranslation('modals');
    const { t: tCommon } = useTranslation('common');
    const [name, setName] = useState('');
    const [presentAlert] = useIonAlert();
    const [presentToast] = useIonToast();
    const createMutation = useCreateUserApiKeyMutation();
    const revokeMutation = useRevokeUserApiKeyMutation();
    const dateFormatter = new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
    });

    const createApiKey = async () => {
        const normalizedName = name.trim();
        if (!normalizedName) {
            return;
        }

        try {
            await createMutation.mutateAsync(normalizedName);
            setName('');
        } catch (error) {
            await presentToast({
                message: getErrorMessage(error, t('api_keys.create_failed')),
                color: 'danger',
                duration: 2500
            });
        }
    };

    const revokeApiKey = async (apiKey: UserApiKey) => {
        try {
            await revokeMutation.mutateAsync(apiKey.id);
            await presentToast({
                message: t('api_keys.revoked'),
                color: 'success',
                icon: checkmarkCircle,
                duration: 2500
            });
        } catch (error) {
            await presentToast({
                message: getErrorMessage(error, t('api_keys.revoke_failed')),
                color: 'danger',
                duration: 2500
            });
        }
    };

    const confirmRevoke = async (apiKey: UserApiKey) => {
        await presentAlert({
            header: t('api_keys.revoke_header'),
            message: t('api_keys.revoke_message', { name: apiKey.name }),
            buttons: [
                { text: tCommon('buttons.cancel'), role: 'cancel' },
                {
                    text: t('api_keys.revoke'),
                    role: 'destructive',
                    handler: () => {
                        void revokeApiKey(apiKey);
                    }
                }
            ]
        });
    };

    const copyCreatedToken = async () => {
        const token = createMutation.data?.token;
        if (!token) {
            return;
        }

        try {
            await copyText(token);
            await presentToast({
                message: t('api_keys.copied'),
                color: 'success',
                icon: checkmarkCircle,
                duration: 2000
            });
        } catch {
            await presentToast({
                message: t('api_keys.copy_failed'),
                color: 'danger',
                duration: 2500
            });
        }
    };

    return (
        <div className={styles.wrapper}>
            <p className={styles.description}>{t('api_keys.description')}</p>

            <IonList className={styles.listCard}>
                <IonItem lines="none">
                    <IonInput
                        label={t('api_keys.name')}
                        labelPlacement="stacked"
                        maxlength={64}
                        value={name}
                        placeholder={t('api_keys.name_placeholder')}
                        disabled={createMutation.isPending}
                        onIonInput={event => setName(event.detail.value ?? '')}
                    />
                </IonItem>
            </IonList>
            <IonButton
                className={styles.createButton}
                expand="block"
                shape="round"
                disabled={!name.trim() || createMutation.isPending}
                onClick={() => void createApiKey()}
            >
                {t('api_keys.create')}
            </IonButton>

            {createMutation.data && (
                <section className={styles.secretCard}>
                    <div className={styles.secretHeader}>
                        <IonIcon icon={keyOutline} color="primary" />
                        <strong>{t('api_keys.created_title')}</strong>
                    </div>
                    <p>{t('api_keys.created_description')}</p>
                    <IonTextarea
                        className={styles.secretValue}
                        readonly
                        autoGrow
                        value={createMutation.data.token}
                        aria-label={t('api_keys.created_title')}
                    />
                    <div className={styles.secretActions}>
                        <IonButton fill="outline" onClick={() => void copyCreatedToken()}>
                            <IonIcon slot="start" icon={copyOutline} />
                            {t('api_keys.copy')}
                        </IonButton>
                        <IonButton fill="clear" onClick={() => createMutation.reset()}>
                            {t('api_keys.done')}
                        </IonButton>
                    </div>
                </section>
            )}

            <section className={styles.keysSection}>
                <h2>{t('api_keys.active_keys')}</h2>
                {apiKeys.length === 0 ? (
                    <IonNote className={styles.empty}>{t('api_keys.empty')}</IonNote>
                ) : (
                    <IonList className={styles.listCard}>
                        {apiKeys.map(apiKey => (
                            <IonItem key={apiKey.id}>
                                <IonIcon slot="start" icon={keyOutline} color="primary" />
                                <IonLabel>
                                    <h2>{apiKey.name}</h2>
                                    <p className={styles.keyPrefix}>{apiKey.keyPrefix}_…</p>
                                    <p>
                                        {t('api_keys.created_at', { date: dateFormatter.format(apiKey.createdAt) })}
                                    </p>
                                    <p>
                                        {apiKey.lastUsedAt
                                            ? t('api_keys.last_used_at', { date: dateFormatter.format(apiKey.lastUsedAt) })
                                            : t('api_keys.never_used')}
                                    </p>
                                </IonLabel>
                                <IonButton
                                    slot="end"
                                    fill="clear"
                                    color="danger"
                                    aria-label={t('api_keys.revoke_named', { name: apiKey.name })}
                                    disabled={revokeMutation.isPending}
                                    onClick={() => void confirmRevoke(apiKey)}
                                >
                                    <IonIcon slot="icon-only" icon={trashOutline} />
                                </IonButton>
                            </IonItem>
                        ))}
                    </IonList>
                )}
            </section>
        </div>
    );
};

export default ApiKeysForm;
